import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { MealPhotoQueueResponse } from '@/api/generated/MNT';
import { PatientID } from '@/components/PatientID';
import { QueueSummaryIcons } from '@/components/QueueSummaryIcons';
import { QueueRegionChip } from '@/components/queue/QueueRegionChip';
import { Card, CardHeader, CardTitle, DataTable, DataTableColumn } from '@/components/ui';
import { RegionScope } from '@/context/RegionScope';
import { HumanTime } from '@/food-editor/components/HumanTime';
import { CombinedQueueItem } from '@/services/useCombinedQueue';
import { getMaskedClinicType } from '@/utils';
import { queueItemPath } from '@/utils/queueItemRoute';

export const ItemTable = (props: { items: CombinedQueueItem[]; loading: boolean }) => {
  const { items, loading } = props;
  const navigate = useNavigate();

  const columns = useMemo<DataTableColumn<CombinedQueueItem>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        value: item => item.id,
        cell: item => (
          <>
            {item.id} <QueueRegionChip region={item.region} />
          </>
        ),
      },
      {
        id: 'priority',
        header: 'Priority',
        cell: item => (
          <div className="w-12">
            <QueueSummaryIcons queue={item as MealPhotoQueueResponse} />
          </div>
        ),
      },
      {
        id: 'patient',
        header: 'Patient',
        value: item => item.patient_id,
        cell: item => (
          <RegionScope region={item.region} showAlerts={false}>
            <PatientID userId={item.patient_id} hideOffline isPriority={!!item.is_priority_patient} />
          </RegionScope>
        ),
      },
      {
        id: 'age',
        header: 'Age',
        value: item => item.created_time,
        cell: item => (
          <span>
            <HumanTime value={item.created_time} />
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        value: item => (item.queue_type === 'nlp_queue' ? 'nlp item' : 'photo item'),
      },
      {
        id: 'clinic_type',
        header: 'Clinic Type',
        value: item => getMaskedClinicType(item.clinic_type),
      },
      { id: 'review_group', header: 'Review Group', value: item => item.data_reviewer_group_id },
      { id: 'reviewer', header: 'Reviewer', value: item => item.first_reviewer_user_id },
    ],
    [],
  );

  const handleRowClick = (row: CombinedQueueItem) => {
    navigate(queueItemPath(row.id, row.region, { utm_source: 'all-queue-table' }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>All items</CardTitle>
      </CardHeader>
      <DataTable
        aria-label="All queue items"
        columns={columns}
        rows={items}
        getRowId={item => item.regionalKey}
        pageSize={25}
        loading={loading}
        emptyMessage="There's nothing in the queue"
        onRowClick={handleRowClick}
      />
    </Card>
  );
};
